import { lazy } from "react";
import { createBrowserRouter } from "react-router";
import { RequireAuth } from "../auth/RequireAuth.js";
import { RouteSuspense } from "./RouteSuspense.js";
import { NotFoundPage, RouteErrorPage } from "../pages/not-found/NotFoundPage.js";

// Lazy-loaded per module, per `07_UI_UX_SPEC.md` §27 ("Use nested routing and lazy-loading by
// module") — each route ships its own chunk instead of one large eagerly-bundled app.
const AppLayout = lazy(() => import("../layouts/AppLayout.js").then((m) => ({ default: m.AppLayout })));
// `/` branches on the account: parent/guardian and student portal accounts get the portal home,
// staff keep `DashboardPage` (see `pages/home/HomePage.tsx`).
const HomePage = lazy(() => import("../pages/home/HomePage.js").then((m) => ({ default: m.HomePage })));
const LoginPage = lazy(() => import("../pages/login/LoginPage.js").then((m) => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() => import("../pages/auth/ForgotPasswordPage.js").then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("../pages/auth/ResetPasswordPage.js").then((m) => ({ default: m.ResetPasswordPage })));
const SettingsPage = lazy(() => import("../pages/settings/SettingsPage.js").then((m) => ({ default: m.SettingsPage })));
const OnboardingPage = lazy(() => import("../pages/onboarding/OnboardingPage.js").then((m) => ({ default: m.OnboardingPage })));
const ProfilePage = lazy(() => import("../pages/profile/ProfilePage.js").then((m) => ({ default: m.ProfilePage })));
const HelpPage = lazy(() => import("../pages/help/HelpPage.js").then((m) => ({ default: m.HelpPage })));
const HelpTopicPage = lazy(() => import("../pages/help/HelpTopicPage.js").then((m) => ({ default: m.HelpTopicPage })));
const UsersPage = lazy(() => import("../pages/admin/users/UsersPage.js").then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import("../pages/admin/roles/RolesPage.js").then((m) => ({ default: m.RolesPage })));
const AuditLogPage = lazy(() => import("../pages/audit/AuditLogPage.js").then((m) => ({ default: m.AuditLogPage })));
const AcademicSetupPage = lazy(() => import("../pages/academics/AcademicSetupPage.js").then((m) => ({ default: m.AcademicSetupPage })));
const AdmissionsPage = lazy(() => import("../pages/admissions/AdmissionsPage.js").then((m) => ({ default: m.AdmissionsPage })));
const ApplicantDetailPage = lazy(() => import("../pages/admissions/ApplicantDetailPage.js").then((m) => ({ default: m.ApplicantDetailPage })));
const InquiryDetailPage = lazy(() => import("../pages/admissions/InquiryDetailPage.js").then((m) => ({ default: m.InquiryDetailPage })));
const AdmissionCycleDetailPage = lazy(() => import("../pages/admissions/AdmissionCycleDetailPage.js").then((m) => ({ default: m.AdmissionCycleDetailPage })));
const StudentsListPage = lazy(() => import("../pages/students/StudentsListPage.js").then((m) => ({ default: m.StudentsListPage })));
const StudentDetailPage = lazy(() => import("../pages/students/StudentDetailPage.js").then((m) => ({ default: m.StudentDetailPage })));
const PromotionsPage = lazy(() => import("../pages/students/PromotionsPage.js").then((m) => ({ default: m.PromotionsPage })));
const GuardianRequestsPage = lazy(() => import("../pages/students/GuardianRequestsPage.js").then((m) => ({ default: m.GuardianRequestsPage })));
const CustomFieldDefinitionsPage = lazy(() =>
  import("../pages/students/CustomFieldDefinitionsPage.js").then((m) => ({ default: m.CustomFieldDefinitionsPage })),
);
const TimetablesPage = lazy(() => import("../pages/timetables/TimetablesPage.js").then((m) => ({ default: m.TimetablesPage })));
const TimetableBuilderPage = lazy(() => import("../pages/timetables/TimetableBuilderPage.js").then((m) => ({ default: m.TimetableBuilderPage })));
const MySchedulePage = lazy(() => import("../pages/timetables/MySchedulePage.js").then((m) => ({ default: m.MySchedulePage })));
const SubstitutionsPage = lazy(() => import("../pages/timetables/SubstitutionsPage.js").then((m) => ({ default: m.SubstitutionsPage })));
const LessonPlansPage = lazy(() => import("../pages/timetables/LessonPlansPage.js").then((m) => ({ default: m.LessonPlansPage })));
const TeacherWorkloadPage = lazy(() => import("../pages/timetables/TeacherWorkloadPage.js").then((m) => ({ default: m.TeacherWorkloadPage })));
const CurriculumProgressPage = lazy(() => import("../pages/timetables/CurriculumProgressPage.js").then((m) => ({ default: m.CurriculumProgressPage })));
const AttendancePage = lazy(() => import("../pages/attendance/AttendancePage.js").then((m) => ({ default: m.AttendancePage })));
const AttendanceCorrectionsPage = lazy(() =>
  import("../pages/attendance/AttendanceCorrectionsPage.js").then((m) => ({ default: m.AttendanceCorrectionsPage })),
);
const StaffAttendancePage = lazy(() => import("../pages/attendance/StaffAttendancePage.js").then((m) => ({ default: m.StaffAttendancePage })));
const StudentLeaveRequestsPage = lazy(() => import("../pages/attendance/StudentLeaveRequestsPage.js").then((m) => ({ default: m.StudentLeaveRequestsPage })));
const AssignmentsPage = lazy(() => import("../pages/assignments/AssignmentsPage.js").then((m) => ({ default: m.AssignmentsPage })));
const AssignmentDetailPage = lazy(() => import("../pages/assignments/AssignmentDetailPage.js").then((m) => ({ default: m.AssignmentDetailPage })));
const LearningResourcesPage = lazy(() => import("../pages/assignments/LearningResourcesPage.js").then((m) => ({ default: m.LearningResourcesPage })));
const ExamTermsPage = lazy(() => import("../pages/exams/ExamTermsPage.js").then((m) => ({ default: m.ExamTermsPage })));
const ExamTermDetailPage = lazy(() => import("../pages/exams/ExamTermDetailPage.js").then((m) => ({ default: m.ExamTermDetailPage })));
const MarksEntryPage = lazy(() => import("../pages/exams/MarksEntryPage.js").then((m) => ({ default: m.MarksEntryPage })));
const StudentResultsPage = lazy(() => import("../pages/exams/StudentResultsPage.js").then((m) => ({ default: m.StudentResultsPage })));
const ReportCardPage = lazy(() => import("../pages/exams/ReportCardPage.js").then((m) => ({ default: m.ReportCardPage })));
const TranscriptPage = lazy(() => import("../pages/exams/TranscriptPage.js").then((m) => ({ default: m.TranscriptPage })));
const FeesLandingPage = lazy(() => import("../pages/fees/FeesLandingPage.js").then((m) => ({ default: m.FeesLandingPage })));
const FeeHeadsPage = lazy(() => import("../pages/fees/FeeHeadsPage.js").then((m) => ({ default: m.FeeHeadsPage })));
const FeeStructuresPage = lazy(() => import("../pages/fees/FeeStructuresPage.js").then((m) => ({ default: m.FeeStructuresPage })));
const FeeStructureDetailPage = lazy(() => import("../pages/fees/FeeStructureDetailPage.js").then((m) => ({ default: m.FeeStructureDetailPage })));
const InvoicesPage = lazy(() => import("../pages/fees/InvoicesPage.js").then((m) => ({ default: m.InvoicesPage })));
const InvoiceDetailPage = lazy(() => import("../pages/fees/InvoiceDetailPage.js").then((m) => ({ default: m.InvoiceDetailPage })));
const RefundsPage = lazy(() => import("../pages/fees/RefundsPage.js").then((m) => ({ default: m.RefundsPage })));
const DiscountsAndFinesPage = lazy(() => import("../pages/fees/DiscountsAndFinesPage.js").then((m) => ({ default: m.DiscountsAndFinesPage })));
const StudentFeeStatementPage = lazy(() => import("../pages/fees/StudentFeeStatementPage.js").then((m) => ({ default: m.StudentFeeStatementPage })));
const PaymentGatewaySettingsPage = lazy(() => import("../pages/fees/PaymentGatewaySettingsPage.js").then((m) => ({ default: m.PaymentGatewaySettingsPage })));
const SandboxCheckoutPage = lazy(() => import("../pages/fees/SandboxCheckoutPage.js").then((m) => ({ default: m.SandboxCheckoutPage })));
const CollectPaymentPage = lazy(() => import("../pages/fees/CollectPaymentPage.js").then((m) => ({ default: m.CollectPaymentPage })));
const GatewayReconciliationPage = lazy(() => import("../pages/fees/GatewayReconciliationPage.js").then((m) => ({ default: m.GatewayReconciliationPage })));
const GatewayReturnPage = lazy(() => import("../pages/fees/GatewayReturnPage.js").then((m) => ({ default: m.GatewayReturnPage })));
const AccountingLandingPage = lazy(() => import("../pages/accounting/AccountingLandingPage.js").then((m) => ({ default: m.AccountingLandingPage })));
const ChartOfAccountsPage = lazy(() => import("../pages/accounting/ChartOfAccountsPage.js").then((m) => ({ default: m.ChartOfAccountsPage })));
const AccountingPeriodsPage = lazy(() => import("../pages/accounting/AccountingPeriodsPage.js").then((m) => ({ default: m.AccountingPeriodsPage })));
const JournalsPage = lazy(() => import("../pages/accounting/JournalsPage.js").then((m) => ({ default: m.JournalsPage })));
const JournalDetailPage = lazy(() => import("../pages/accounting/JournalDetailPage.js").then((m) => ({ default: m.JournalDetailPage })));
const ExpensesPage = lazy(() => import("../pages/accounting/ExpensesPage.js").then((m) => ({ default: m.ExpensesPage })));
const AccountingReportsPage = lazy(() => import("../pages/accounting/AccountingReportsPage.js").then((m) => ({ default: m.AccountingReportsPage })));
const HrLandingPage = lazy(() => import("../pages/hr/HrLandingPage.js").then((m) => ({ default: m.HrLandingPage })));
const EmployeesPage = lazy(() => import("../pages/hr/EmployeesPage.js").then((m) => ({ default: m.EmployeesPage })));
const EmployeeDetailPage = lazy(() => import("../pages/hr/EmployeeDetailPage.js").then((m) => ({ default: m.EmployeeDetailPage })));
const DepartmentsPage = lazy(() => import("../pages/hr/DepartmentsPage.js").then((m) => ({ default: m.DepartmentsPage })));
const DesignationsPage = lazy(() => import("../pages/hr/DesignationsPage.js").then((m) => ({ default: m.DesignationsPage })));
const LeaveTypesPage = lazy(() => import("../pages/hr/LeaveTypesPage.js").then((m) => ({ default: m.LeaveTypesPage })));
const LeaveRequestsPage = lazy(() => import("../pages/hr/LeaveRequestsPage.js").then((m) => ({ default: m.LeaveRequestsPage })));
const PayrollLandingPage = lazy(() => import("../pages/payroll/PayrollLandingPage.js").then((m) => ({ default: m.PayrollLandingPage })));
const SalaryComponentsPage = lazy(() => import("../pages/payroll/SalaryComponentsPage.js").then((m) => ({ default: m.SalaryComponentsPage })));
const SalaryStructuresPage = lazy(() => import("../pages/payroll/SalaryStructuresPage.js").then((m) => ({ default: m.SalaryStructuresPage })));
const PayrollPeriodsPage = lazy(() => import("../pages/payroll/PayrollPeriodsPage.js").then((m) => ({ default: m.PayrollPeriodsPage })));
const PayrollPeriodDetailPage = lazy(() => import("../pages/payroll/PayrollPeriodDetailPage.js").then((m) => ({ default: m.PayrollPeriodDetailPage })));
const PayrollRunDetailPage = lazy(() => import("../pages/payroll/PayrollRunDetailPage.js").then((m) => ({ default: m.PayrollRunDetailPage })));
const MyPayslipsPage = lazy(() => import("../pages/payroll/MyPayslipsPage.js").then((m) => ({ default: m.MyPayslipsPage })));
const LibraryLandingPage = lazy(() => import("../pages/library/LibraryLandingPage.js").then((m) => ({ default: m.LibraryLandingPage })));
const BooksPage = lazy(() => import("../pages/library/BooksPage.js").then((m) => ({ default: m.BooksPage })));
const BookDetailPage = lazy(() => import("../pages/library/BookDetailPage.js").then((m) => ({ default: m.BookDetailPage })));
const CirculationDeskPage = lazy(() => import("../pages/library/CirculationDeskPage.js").then((m) => ({ default: m.CirculationDeskPage })));
const ReservationsPage = lazy(() => import("../pages/library/ReservationsPage.js").then((m) => ({ default: m.ReservationsPage })));
const MyLibraryPage = lazy(() => import("../pages/library/MyLibraryPage.js").then((m) => ({ default: m.MyLibraryPage })));
const TransportLandingPage = lazy(() => import("../pages/transport/TransportLandingPage.js").then((m) => ({ default: m.TransportLandingPage })));
const VehiclesPage = lazy(() => import("../pages/transport/VehiclesPage.js").then((m) => ({ default: m.VehiclesPage })));
const VehicleDetailPage = lazy(() => import("../pages/transport/VehicleDetailPage.js").then((m) => ({ default: m.VehicleDetailPage })));
const RoutesPage = lazy(() => import("../pages/transport/RoutesPage.js").then((m) => ({ default: m.RoutesPage })));
const RouteDetailPage = lazy(() => import("../pages/transport/RouteDetailPage.js").then((m) => ({ default: m.RouteDetailPage })));
const StopsPage = lazy(() => import("../pages/transport/StopsPage.js").then((m) => ({ default: m.StopsPage })));
const StudentAssignmentsPage = lazy(() => import("../pages/transport/StudentAssignmentsPage.js").then((m) => ({ default: m.StudentAssignmentsPage })));
const MyTransportPage = lazy(() => import("../pages/transport/MyTransportPage.js").then((m) => ({ default: m.MyTransportPage })));
const HostelLandingPage = lazy(() => import("../pages/hostel/HostelLandingPage.js").then((m) => ({ default: m.HostelLandingPage })));
const HostelsPage = lazy(() => import("../pages/hostel/HostelsPage.js").then((m) => ({ default: m.HostelsPage })));
const HostelDetailPage = lazy(() => import("../pages/hostel/HostelDetailPage.js").then((m) => ({ default: m.HostelDetailPage })));
const AllocationsPage = lazy(() => import("../pages/hostel/AllocationsPage.js").then((m) => ({ default: m.AllocationsPage })));
const MyHostelPage = lazy(() => import("../pages/hostel/MyHostelPage.js").then((m) => ({ default: m.MyHostelPage })));
const InventoryLandingPage = lazy(() => import("../pages/inventory/InventoryLandingPage.js").then((m) => ({ default: m.InventoryLandingPage })));
const SuppliersPage = lazy(() => import("../pages/inventory/SuppliersPage.js").then((m) => ({ default: m.SuppliersPage })));
const ItemsPage = lazy(() => import("../pages/inventory/ItemsPage.js").then((m) => ({ default: m.ItemsPage })));
const ItemDetailPage = lazy(() => import("../pages/inventory/ItemDetailPage.js").then((m) => ({ default: m.ItemDetailPage })));
const StoresPage = lazy(() => import("../pages/inventory/StoresPage.js").then((m) => ({ default: m.StoresPage })));
const StockDeskPage = lazy(() => import("../pages/inventory/StockDeskPage.js").then((m) => ({ default: m.StockDeskPage })));
const ProcurementLandingPage = lazy(() => import("../pages/procurement/ProcurementLandingPage.js").then((m) => ({ default: m.ProcurementLandingPage })));
const PurchaseRequestsPage = lazy(() => import("../pages/procurement/PurchaseRequestsPage.js").then((m) => ({ default: m.PurchaseRequestsPage })));
const PurchaseRequestDetailPage = lazy(() =>
  import("../pages/procurement/PurchaseRequestDetailPage.js").then((m) => ({ default: m.PurchaseRequestDetailPage })),
);
const PurchaseOrdersPage = lazy(() => import("../pages/procurement/PurchaseOrdersPage.js").then((m) => ({ default: m.PurchaseOrdersPage })));
const PurchaseOrderDetailPage = lazy(() => import("../pages/procurement/PurchaseOrderDetailPage.js").then((m) => ({ default: m.PurchaseOrderDetailPage })));
const GoodsReceiptsPage = lazy(() => import("../pages/procurement/GoodsReceiptsPage.js").then((m) => ({ default: m.GoodsReceiptsPage })));
const GoodsReceiptDetailPage = lazy(() => import("../pages/procurement/GoodsReceiptDetailPage.js").then((m) => ({ default: m.GoodsReceiptDetailPage })));

const CommunicationLandingPage = lazy(() =>
  import("../pages/communication/CommunicationLandingPage.js").then((m) => ({ default: m.CommunicationLandingPage })),
);
const NoticesPage = lazy(() => import("../pages/communication/NoticesPage.js").then((m) => ({ default: m.NoticesPage })));
const NoticeDetailPage = lazy(() => import("../pages/communication/NoticeDetailPage.js").then((m) => ({ default: m.NoticeDetailPage })));
const NotificationTemplatesPage = lazy(() =>
  import("../pages/communication/NotificationTemplatesPage.js").then((m) => ({ default: m.NotificationTemplatesPage })),
);
const NotificationDeliveriesPage = lazy(() =>
  import("../pages/communication/NotificationDeliveriesPage.js").then((m) => ({ default: m.NotificationDeliveriesPage })),
);
const MyNotificationsPage = lazy(() => import("../pages/communication/MyNotificationsPage.js").then((m) => ({ default: m.MyNotificationsPage })));
const CalendarEventsPage = lazy(() => import("../pages/communication/CalendarEventsPage.js").then((m) => ({ default: m.CalendarEventsPage })));
const MyCalendarPage = lazy(() => import("../pages/communication/MyCalendarPage.js").then((m) => ({ default: m.MyCalendarPage })));
const ParentTeacherThreadsPage = lazy(() =>
  import("../pages/communication/ParentTeacherThreadsPage.js").then((m) => ({ default: m.ParentTeacherThreadsPage })),
);
const ParentTeacherThreadDetailPage = lazy(() =>
  import("../pages/communication/ParentTeacherThreadDetailPage.js").then((m) => ({ default: m.ParentTeacherThreadDetailPage })),
);

const CertificatesPage = lazy(() => import("../pages/certificates/CertificatesPage.js").then((m) => ({ default: m.CertificatesPage })));
const CertificateDetailPage = lazy(() => import("../pages/certificates/CertificateDetailPage.js").then((m) => ({ default: m.CertificateDetailPage })));
const CertificateTemplatesPage = lazy(() => import("../pages/certificates/CertificateTemplatesPage.js").then((m) => ({ default: m.CertificateTemplatesPage })));
const CertificateTemplateDetailPage = lazy(() =>
  import("../pages/certificates/CertificateTemplateDetailPage.js").then((m) => ({ default: m.CertificateTemplateDetailPage })),
);
const VerifyCertificatePage = lazy(() => import("../pages/certificates/VerifyCertificatePage.js").then((m) => ({ default: m.VerifyCertificatePage })));

const DisciplineIncidentsPage = lazy(() => import("../pages/discipline/DisciplineIncidentsPage.js").then((m) => ({ default: m.DisciplineIncidentsPage })));
const DisciplineIncidentDetailPage = lazy(() =>
  import("../pages/discipline/DisciplineIncidentDetailPage.js").then((m) => ({ default: m.DisciplineIncidentDetailPage })),
);
const BehaviourNoticesPage = lazy(() => import("../pages/discipline/BehaviourNoticesPage.js").then((m) => ({ default: m.BehaviourNoticesPage })));
const HealthVisitsPage = lazy(() => import("../pages/health-office/HealthVisitsPage.js").then((m) => ({ default: m.HealthVisitsPage })));
const HealthVisitDetailPage = lazy(() => import("../pages/health-office/HealthVisitDetailPage.js").then((m) => ({ default: m.HealthVisitDetailPage })));
const VaccinationRecordsPage = lazy(() => import("../pages/health-office/VaccinationRecordsPage.js").then((m) => ({ default: m.VaccinationRecordsPage })));
const VisitorsPage = lazy(() => import("../pages/visitors/VisitorsPage.js").then((m) => ({ default: m.VisitorsPage })));

const ReportsCatalogPage = lazy(() => import("../pages/reports/ReportsCatalogPage.js").then((m) => ({ default: m.ReportsCatalogPage })));
const ReportRunnerPage = lazy(() => import("../pages/reports/ReportRunnerPage.js").then((m) => ({ default: m.ReportRunnerPage })));
const ExecutiveDashboardPage = lazy(() => import("../pages/reports/ExecutiveDashboardPage.js").then((m) => ({ default: m.ExecutiveDashboardPage })));
const MyExportsPage = lazy(() => import("../pages/reports/MyExportsPage.js").then((m) => ({ default: m.MyExportsPage })));
const ExportDetailPage = lazy(() => import("../pages/reports/ExportDetailPage.js").then((m) => ({ default: m.ExportDetailPage })));
const ImportsPage = lazy(() => import("../pages/reports/ImportsPage.js").then((m) => ({ default: m.ImportsPage })));
const ImportDetailPage = lazy(() => import("../pages/reports/ImportDetailPage.js").then((m) => ({ default: m.ImportDetailPage })));

// Parent/guardian and student portal — per-child pages (`routes/portal-nav.tsx` builds their menu).
const PortalAttendancePage = lazy(() => import("../pages/portal/PortalAttendancePage.js").then((m) => ({ default: m.PortalAttendancePage })));
const PortalAssignmentsPage = lazy(() => import("../pages/portal/PortalAssignmentsPage.js").then((m) => ({ default: m.PortalAssignmentsPage })));
const PortalTimetablePage = lazy(() => import("../pages/portal/PortalTimetablePage.js").then((m) => ({ default: m.PortalTimetablePage })));
const PortalResultsPage = lazy(() => import("../pages/portal/PortalResultsPage.js").then((m) => ({ default: m.PortalResultsPage })));
const PortalFeesPage = lazy(() => import("../pages/portal/PortalFeesPage.js").then((m) => ({ default: m.PortalFeesPage })));
const PortalLeavePage = lazy(() => import("../pages/portal/PortalLeavePage.js").then((m) => ({ default: m.PortalLeavePage })));
const PortalGuardianRequestsPage = lazy(() => import("../pages/portal/PortalGuardianRequestsPage.js").then((m) => ({ default: m.PortalGuardianRequestsPage })));
const PortalTranscriptPage = lazy(() => import("../pages/portal/PortalTranscriptPage.js").then((m) => ({ default: m.PortalTranscriptPage })));

const PORTAL_CHILD_ROUTES = [
  { path: "portal/students/:studentId/attendance", Page: PortalAttendancePage },
  { path: "portal/students/:studentId/assignments", Page: PortalAssignmentsPage },
  { path: "portal/students/:studentId/timetable", Page: PortalTimetablePage },
  { path: "portal/students/:studentId/results", Page: PortalResultsPage },
  { path: "portal/students/:studentId/transcript", Page: PortalTranscriptPage },
  { path: "portal/students/:studentId/fees", Page: PortalFeesPage },
  { path: "portal/students/:studentId/leave", Page: PortalLeavePage },
  { path: "portal/students/:studentId/guardian-requests", Page: PortalGuardianRequestsPage },
];

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <RouteSuspense>
        <LoginPage />
      </RouteSuspense>
    ),
  },
  {
    path: "/auth/forgot-password",
    element: (
      <RouteSuspense>
        <ForgotPasswordPage />
      </RouteSuspense>
    ),
  },
  {
    path: "/auth/reset-password",
    element: (
      <RouteSuspense>
        <ResetPasswordPage />
      </RouteSuspense>
    ),
  },
  {
    // The link in a portal invite email (student/guardian "Set your password") — same one-time
    // token endpoint as a reset, different copy.
    path: "/auth/accept-invite",
    element: (
      <RouteSuspense>
        <ResetPasswordPage mode="invite" />
      </RouteSuspense>
    ),
  },
  {
    // ADR-0022 §10 — public verification (QR code / printed link), so no `RequireAuth`.
    path: "/verify-certificate",
    element: (
      <RouteSuspense>
        <VerifyCertificatePage />
      </RouteSuspense>
    ),
  },
  {
    path: "/",
    errorElement: <RouteErrorPage />,
    element: (
      <RequireAuth>
        <RouteSuspense>
          <AppLayout />
        </RouteSuspense>
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <RouteSuspense>
            <HomePage />
          </RouteSuspense>
        ),
      },
      ...PORTAL_CHILD_ROUTES.map(({ path, Page }) => ({
        path,
        element: (
          <RouteSuspense>
            <Page />
          </RouteSuspense>
        ),
      })),
      {
        path: "profile",
        element: (
          <RouteSuspense>
            <ProfilePage />
          </RouteSuspense>
        ),
      },
      {
        // KG-13: the header/footer Help link — user-guide chapters for this user's role.
        path: "help",
        element: (
          <RouteSuspense>
            <HelpPage />
          </RouteSuspense>
        ),
      },
      {
        // KG-13: one guide page, rendered in the app from the bundled Markdown (the repository is private).
        path: "help/*",
        element: (
          <RouteSuspense>
            <HelpTopicPage />
          </RouteSuspense>
        ),
      },
      {
        path: "admin/users",
        element: (
          <RouteSuspense>
            <UsersPage />
          </RouteSuspense>
        ),
      },
      {
        path: "admin/roles",
        element: (
          <RouteSuspense>
            <RolesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "audit",
        element: (
          <RouteSuspense>
            <AuditLogPage />
          </RouteSuspense>
        ),
      },
      {
        path: "academics/setup",
        element: (
          <RouteSuspense>
            <AcademicSetupPage />
          </RouteSuspense>
        ),
      },
      {
        path: "admissions",
        element: (
          <RouteSuspense>
            <AdmissionsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "admissions/applicants/:id",
        element: (
          <RouteSuspense>
            <ApplicantDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "admissions/inquiries/:id",
        element: (
          <RouteSuspense>
            <InquiryDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "admissions/cycles/:id",
        element: (
          <RouteSuspense>
            <AdmissionCycleDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "students",
        element: (
          <RouteSuspense>
            <StudentsListPage />
          </RouteSuspense>
        ),
      },
      {
        path: "students/promotions",
        element: (
          <RouteSuspense>
            <PromotionsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "students/guardian-requests",
        element: (
          <RouteSuspense>
            <GuardianRequestsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "students/custom-fields",
        element: (
          <RouteSuspense>
            <CustomFieldDefinitionsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "students/:id",
        element: (
          <RouteSuspense>
            <StudentDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "students/:id/report-card",
        element: (
          <RouteSuspense>
            <ReportCardPage />
          </RouteSuspense>
        ),
      },
      {
        path: "students/:id/transcript",
        element: (
          <RouteSuspense>
            <TranscriptPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/fees",
        element: (
          <RouteSuspense>
            <FeesLandingPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/fees/fee-heads",
        element: (
          <RouteSuspense>
            <FeeHeadsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/fees/structures",
        element: (
          <RouteSuspense>
            <FeeStructuresPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/fees/structures/:id",
        element: (
          <RouteSuspense>
            <FeeStructureDetailPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `finance/fees/invoices/:id` so the static "invoices" list route isn't
        // shadowed — same reasoning as `assignments/learning-resources` above `assignments/:id`.
        path: "finance/fees/invoices",
        element: (
          <RouteSuspense>
            <InvoicesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/fees/invoices/:id",
        element: (
          <RouteSuspense>
            <InvoiceDetailPage />
          </RouteSuspense>
        ),
      },
      {
        // The cashier's "Collect a payment" entry (dashboard tile): pick a student, collect, print the receipt.
        path: "finance/fees/collect",
        element: (
          <RouteSuspense>
            <CollectPaymentPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/fees/refunds",
        element: (
          <RouteSuspense>
            <RefundsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/fees/discounts",
        element: (
          <RouteSuspense>
            <DiscountsAndFinesPage />
          </RouteSuspense>
        ),
      },
      {
        // The portal's own entry point (no `:id` — falls back to `user.studentId`), registered
        // before `finance/fees/students/:id` so "statement" is never mistaken for a student id.
        path: "finance/fees/statement",
        element: (
          <RouteSuspense>
            <StudentFeeStatementPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/fees/students/:id",
        element: (
          <RouteSuspense>
            <StudentFeeStatementPage />
          </RouteSuspense>
        ),
      },
      {
        // KG-94: gateway payments compared with the provider (pending, mismatched, settled) with row actions.
        path: "finance/fees/gateway-reconciliation",
        element: (
          <RouteSuspense>
            <GatewayReconciliationPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/fees/gateway-settings",
        element: (
          <RouteSuspense>
            <PaymentGatewaySettingsPage />
          </RouteSuspense>
        ),
      },
      {
        // The sandbox provider's own hosted-checkout page (ADR-0015 §4) — only ever reached via
        // the `redirectUrl` from `POST /payments/gateway/initiate`, never browsed to directly, so
        // it carries no nav entry. Still nested under the normal authenticated shell: the user must
        // already be logged in to hold a payment id, and this app has no separate portal-only
        // layout to prefer over the one every other fees page already uses.
        path: "pay/sandbox/:paymentId",
        element: (
          <RouteSuspense>
            <SandboxCheckoutPage />
          </RouteSuspense>
        ),
      },
      {
        // Where a payment provider sends the payer back after checkout (`PaymentGatewayService.initiate`'s
        // `returnUrl`). The page asks the server to re-verify with the provider; the redirect alone proves nothing.
        path: "pay/return/:paymentId",
        element: (
          <RouteSuspense>
            <GatewayReturnPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/accounting",
        element: (
          <RouteSuspense>
            <AccountingLandingPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/accounting/chart-of-accounts",
        element: (
          <RouteSuspense>
            <ChartOfAccountsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/accounting/periods",
        element: (
          <RouteSuspense>
            <AccountingPeriodsPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `finance/accounting/journals/:id` so the static "journals" list route
        // isn't shadowed — same reasoning as `assignments/learning-resources` above `assignments/:id`.
        path: "finance/accounting/journals",
        element: (
          <RouteSuspense>
            <JournalsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/accounting/journals/:id",
        element: (
          <RouteSuspense>
            <JournalDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/accounting/expenses",
        element: (
          <RouteSuspense>
            <ExpensesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "finance/accounting/reports",
        element: (
          <RouteSuspense>
            <AccountingReportsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "hr",
        element: (
          <RouteSuspense>
            <HrLandingPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `hr/employees/:id` so the static "employees" list route isn't
        // shadowed — same reasoning as `assignments/learning-resources` above `assignments/:id`.
        path: "hr/employees",
        element: (
          <RouteSuspense>
            <EmployeesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "hr/employees/:id",
        element: (
          <RouteSuspense>
            <EmployeeDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "hr/departments",
        element: (
          <RouteSuspense>
            <DepartmentsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "hr/designations",
        element: (
          <RouteSuspense>
            <DesignationsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "hr/leave-types",
        element: (
          <RouteSuspense>
            <LeaveTypesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "hr/leave-requests",
        element: (
          <RouteSuspense>
            <LeaveRequestsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "payroll",
        element: (
          <RouteSuspense>
            <PayrollLandingPage />
          </RouteSuspense>
        ),
      },
      {
        path: "payroll/salary-components",
        element: (
          <RouteSuspense>
            <SalaryComponentsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "payroll/salary-structures",
        element: (
          <RouteSuspense>
            <SalaryStructuresPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `payroll/periods/:id` so the static "periods" list route isn't
        // shadowed — same reasoning as `assignments/learning-resources` above `assignments/:id`.
        path: "payroll/periods",
        element: (
          <RouteSuspense>
            <PayrollPeriodsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "payroll/periods/:id",
        element: (
          <RouteSuspense>
            <PayrollPeriodDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "payroll/runs/:id",
        element: (
          <RouteSuspense>
            <PayrollRunDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "payroll/my-payslips",
        element: (
          <RouteSuspense>
            <MyPayslipsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "library",
        element: (
          <RouteSuspense>
            <LibraryLandingPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `library/books/:id` so the static "books" list route isn't
        // shadowed — same reasoning as `hr/employees` above `hr/employees/:id`.
        path: "library/books",
        element: (
          <RouteSuspense>
            <BooksPage />
          </RouteSuspense>
        ),
      },
      {
        path: "library/books/:id",
        element: (
          <RouteSuspense>
            <BookDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "library/circulation",
        element: (
          <RouteSuspense>
            <CirculationDeskPage />
          </RouteSuspense>
        ),
      },
      {
        path: "library/reservations",
        element: (
          <RouteSuspense>
            <ReservationsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "library/my-library",
        element: (
          <RouteSuspense>
            <MyLibraryPage />
          </RouteSuspense>
        ),
      },
      {
        path: "transport",
        element: (
          <RouteSuspense>
            <TransportLandingPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `transport/vehicles/:id` so the static "vehicles" list route isn't
        // shadowed — same reasoning as `hr/employees` above `hr/employees/:id`.
        path: "transport/vehicles",
        element: (
          <RouteSuspense>
            <VehiclesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "transport/vehicles/:id",
        element: (
          <RouteSuspense>
            <VehicleDetailPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `transport/routes/:id` for the same reason as `transport/vehicles`
        // above `transport/vehicles/:id`.
        path: "transport/routes",
        element: (
          <RouteSuspense>
            <RoutesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "transport/routes/:id",
        element: (
          <RouteSuspense>
            <RouteDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "transport/stops",
        element: (
          <RouteSuspense>
            <StopsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "transport/assignments",
        element: (
          <RouteSuspense>
            <StudentAssignmentsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "transport/my-transport",
        element: (
          <RouteSuspense>
            <MyTransportPage />
          </RouteSuspense>
        ),
      },
      {
        path: "hostel",
        element: (
          <RouteSuspense>
            <HostelLandingPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `hostel/hostels/:id` so the static "hostels" list route isn't
        // shadowed — same reasoning as `transport/vehicles` above `transport/vehicles/:id`.
        path: "hostel/hostels",
        element: (
          <RouteSuspense>
            <HostelsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "hostel/hostels/:id",
        element: (
          <RouteSuspense>
            <HostelDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "hostel/allocations",
        element: (
          <RouteSuspense>
            <AllocationsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "hostel/my-hostel",
        element: (
          <RouteSuspense>
            <MyHostelPage />
          </RouteSuspense>
        ),
      },
      {
        path: "inventory",
        element: (
          <RouteSuspense>
            <InventoryLandingPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `inventory/items/:id` so the static "items" list route isn't
        // shadowed — same reasoning as `transport/vehicles` above `transport/vehicles/:id`.
        path: "inventory/items",
        element: (
          <RouteSuspense>
            <ItemsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "inventory/items/:id",
        element: (
          <RouteSuspense>
            <ItemDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "inventory/stores",
        element: (
          <RouteSuspense>
            <StoresPage />
          </RouteSuspense>
        ),
      },
      {
        path: "inventory/suppliers",
        element: (
          <RouteSuspense>
            <SuppliersPage />
          </RouteSuspense>
        ),
      },
      {
        path: "inventory/stock",
        element: (
          <RouteSuspense>
            <StockDeskPage />
          </RouteSuspense>
        ),
      },
      {
        path: "procurement",
        element: (
          <RouteSuspense>
            <ProcurementLandingPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `procurement/requests/:id` so the static "requests" list route isn't
        // shadowed — same reasoning as `transport/vehicles` above `transport/vehicles/:id`.
        path: "procurement/requests",
        element: (
          <RouteSuspense>
            <PurchaseRequestsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "procurement/requests/:id",
        element: (
          <RouteSuspense>
            <PurchaseRequestDetailPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `procurement/orders/:id` for the same reason as
        // `procurement/requests` above `procurement/requests/:id`.
        path: "procurement/orders",
        element: (
          <RouteSuspense>
            <PurchaseOrdersPage />
          </RouteSuspense>
        ),
      },
      {
        path: "procurement/orders/:id",
        element: (
          <RouteSuspense>
            <PurchaseOrderDetailPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `procurement/receipts/:id` for the same reason as
        // `procurement/requests` above `procurement/requests/:id`.
        path: "procurement/receipts",
        element: (
          <RouteSuspense>
            <GoodsReceiptsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "procurement/receipts/:id",
        element: (
          <RouteSuspense>
            <GoodsReceiptDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "communication",
        element: (
          <RouteSuspense>
            <CommunicationLandingPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `communication/notices/:id` so the static "notices" list route isn't
        // shadowed — same reasoning as `transport/vehicles` above `transport/vehicles/:id`.
        path: "communication/notices",
        element: (
          <RouteSuspense>
            <NoticesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "communication/notices/:id",
        element: (
          <RouteSuspense>
            <NoticeDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "communication/templates",
        element: (
          <RouteSuspense>
            <NotificationTemplatesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "communication/deliveries",
        element: (
          <RouteSuspense>
            <NotificationDeliveriesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "communication/my-notifications",
        element: (
          <RouteSuspense>
            <MyNotificationsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "communication/calendar-events",
        element: (
          <RouteSuspense>
            <CalendarEventsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "communication/my-calendar",
        element: (
          <RouteSuspense>
            <MyCalendarPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `communication/messages/:id` for the same reason as
        // `communication/notices` above `communication/notices/:id`.
        path: "communication/messages",
        element: (
          <RouteSuspense>
            <ParentTeacherThreadsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "communication/messages/:id",
        element: (
          <RouteSuspense>
            <ParentTeacherThreadDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "certificates",
        element: (
          <RouteSuspense>
            <CertificatesPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `certificates/:id` so the static "templates" segment isn't shadowed.
        path: "certificates/templates",
        element: (
          <RouteSuspense>
            <CertificateTemplatesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "certificates/templates/:id",
        element: (
          <RouteSuspense>
            <CertificateTemplateDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "certificates/:id",
        element: (
          <RouteSuspense>
            <CertificateDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "discipline",
        element: (
          <RouteSuspense>
            <DisciplineIncidentsPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `discipline/:id` so the static segment isn't shadowed.
        path: "discipline/behaviour-notices",
        element: (
          <RouteSuspense>
            <BehaviourNoticesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "discipline/:id",
        element: (
          <RouteSuspense>
            <DisciplineIncidentDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "reports",
        element: (
          <RouteSuspense>
            <ReportsCatalogPage />
          </RouteSuspense>
        ),
      },
      {
        // Static segments registered before `reports/:code` so they aren't shadowed.
        path: "reports/dashboard",
        element: (
          <RouteSuspense>
            <ExecutiveDashboardPage />
          </RouteSuspense>
        ),
      },
      {
        path: "reports/exports",
        element: (
          <RouteSuspense>
            <MyExportsPage />
          </RouteSuspense>
        ),
      },
      {
        // The export-ready notification's `actionUrl`.
        path: "reports/exports/:id",
        element: (
          <RouteSuspense>
            <ExportDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "reports/imports",
        element: (
          <RouteSuspense>
            <ImportsPage />
          </RouteSuspense>
        ),
      },
      {
        // The import-completed notification's `actionUrl`.
        path: "reports/imports/:id",
        element: (
          <RouteSuspense>
            <ImportDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "reports/:code",
        element: (
          <RouteSuspense>
            <ReportRunnerPage />
          </RouteSuspense>
        ),
      },
      {
        path: "health-office",
        element: (
          <RouteSuspense>
            <HealthVisitsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "health-office/visits/:id",
        element: (
          <RouteSuspense>
            <HealthVisitDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "health-office/vaccinations",
        element: (
          <RouteSuspense>
            <VaccinationRecordsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "visitors",
        element: (
          <RouteSuspense>
            <VisitorsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "timetable",
        element: (
          <RouteSuspense>
            <TimetablesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "timetable/teachers",
        element: (
          <RouteSuspense>
            <MySchedulePage />
          </RouteSuspense>
        ),
      },
      {
        path: "timetable/versions/:id",
        element: (
          <RouteSuspense>
            <TimetableBuilderPage />
          </RouteSuspense>
        ),
      },
      {
        path: "timetable/substitutions",
        element: (
          <RouteSuspense>
            <SubstitutionsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "timetable/lesson-plans",
        element: (
          <RouteSuspense>
            <LessonPlansPage />
          </RouteSuspense>
        ),
      },
      {
        path: "timetable/workload",
        element: (
          <RouteSuspense>
            <TeacherWorkloadPage />
          </RouteSuspense>
        ),
      },
      {
        path: "timetable/curriculum-progress",
        element: (
          <RouteSuspense>
            <CurriculumProgressPage />
          </RouteSuspense>
        ),
      },
      {
        path: "attendance",
        element: (
          <RouteSuspense>
            <AttendancePage />
          </RouteSuspense>
        ),
      },
      {
        path: "attendance/corrections",
        element: (
          <RouteSuspense>
            <AttendanceCorrectionsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "attendance/staff",
        element: (
          <RouteSuspense>
            <StaffAttendancePage />
          </RouteSuspense>
        ),
      },
      {
        path: "attendance/leave",
        element: (
          <RouteSuspense>
            <StudentLeaveRequestsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "assignments",
        element: (
          <RouteSuspense>
            <AssignmentsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "assignments/learning-resources",
        element: (
          <RouteSuspense>
            <LearningResourcesPage />
          </RouteSuspense>
        ),
      },
      {
        path: "assignments/:id",
        element: (
          <RouteSuspense>
            <AssignmentDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "exams",
        element: (
          <RouteSuspense>
            <ExamTermsPage />
          </RouteSuspense>
        ),
      },
      {
        // Registered before `exams/:id` so the static "results" segment isn't
        // shadowed by the dynamic exam-term id param — same reasoning as
        // `assignments/learning-resources` above `assignments/:id`.
        path: "exams/results",
        element: (
          <RouteSuspense>
            <StudentResultsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "exams/:id",
        element: (
          <RouteSuspense>
            <ExamTermDetailPage />
          </RouteSuspense>
        ),
      },
      {
        path: "exams/:termId/assessments/:assessmentId/marks",
        element: (
          <RouteSuspense>
            <MarksEntryPage />
          </RouteSuspense>
        ),
      },
      {
        path: "settings",
        element: (
          <RouteSuspense>
            <SettingsPage />
          </RouteSuspense>
        ),
      },
      {
        path: "onboarding",
        element: (
          <RouteSuspense>
            <OnboardingPage />
          </RouteSuspense>
        ),
      },
      // Phase 22 — an unknown path inside the app gets a real "page not found", not the router's error screen.
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
